/**
 * Envío AP-4 · escenario de «Editar imagen»: el resultado en vivo (canvas) y,
 * encima, las manijas de la herramienta activa (recorte, guías del fundido,
 * esquinas para deformar, puntos de la forma libre y anotaciones).
 *
 * Ocupa el ancho de su contenedor; el alto sale de la proporción de la imagen.
 * Todas las coordenadas son fracciones 0..1 de la imagen.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import useImageEditStore from '../../store/imageEditStore';
import {
  PREVIEW_PX, cropRect, defaultPolygon, hitMark, insertPolygonPoint, movePoint,
  normalizeParams, outputSize, renderEdit,
} from '../../utils/imageEdit';

const CORNERS = [[0, 0], [1, 0], [1, 1], [0, 1]];
const clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, v));
const pct = (v) => `${v * 100}%`;

/** Qué se dibuja en el canvas según la herramienta (al recortar se ve todo). */
function displayParams(params, tool) {
  if (tool === 'recortar') return { ...params, crop: { t: 0, r: 0, b: 0, l: 0 }, shape: { ...params.shape, kind: 'rect', radius: 0 }, border: { ...params.border, enabled: false } };
  if (tool === 'forma' && params.shape.kind === 'polygon') return { ...params, shape: { ...params.shape, kind: 'rect', radius: 0 }, border: { ...params.border, enabled: false } };
  if (tool === 'deformar') return { ...params, crop: { t: 0, r: 0, b: 0, l: 0 }, shape: { ...params.shape, kind: 'rect', radius: 0 }, fade: { ...params.fade, side: 'none' } };
  return params;
}

export default function ImageEditStage({ img, className = '', style }) {
  const params = useImageEditStore((s) => s.params);
  const tool = useImageEditStore((s) => s.tool);
  const markTool = useImageEditStore((s) => s.markTool);
  const markColor = useImageEditStore((s) => s.markColor);
  const markWidth = useImageEditStore((s) => s.markWidth);
  const update = useImageEditStore((s) => s.update);
  const checkpoint = useImageEditStore((s) => s.checkpoint);

  const wrapRef = useRef(null);
  const canvasRef = useRef(null);
  const [drag, setDrag] = useState(null);        // { kind, index?, start, origin }
  const [draft, setDraftState] = useState(null); // marca en curso
  const draftRef = useRef(null);
  const setDraft = useCallback((next) => {
    draftRef.current = typeof next === 'function' ? next(draftRef.current) : next;
    setDraftState(draftRef.current);
  }, []);
  const [textAt, setTextAt] = useState(null);    // [x, y] del texto que se está escribiendo
  const [textVal, setTextVal] = useState('');
  const [boxPx, setBoxPx] = useState({ w: 600, h: 340 });

  const size = useMemo(() => (img
    ? outputSize(img.naturalWidth || img.width, img.naturalHeight || img.height, params.rotate, PREVIEW_PX)
    : { width: 16, height: 9 }), [img, params.rotate]);

  // Medida en pantalla (grosores de las marcas en vivo)
  useEffect(() => {
    const el = wrapRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(() => setBoxPx({ w: el.clientWidth || 600, h: el.clientHeight || 340 }));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Render en vivo (más liviano mientras se arrastran las esquinas)
  const shown = useMemo(() => displayParams(params, tool), [params, tool]);
  const lowRes = drag?.kind === 'warp';
  useEffect(() => {
    if (!img) return undefined;
    const raf = requestAnimationFrame(() => {
      const out = renderEdit(img, shown, { maxPx: lowRes ? 520 : PREVIEW_PX });
      const c = canvasRef.current;
      if (!c) return;
      c.width = out.width; c.height = out.height;
      c.getContext('2d').drawImage(out, 0, 0);
    });
    return () => cancelAnimationFrame(raf);
  }, [img, shown, lowRes]);

  const toFrac = useCallback((e) => {
    const r = wrapRef.current.getBoundingClientRect();
    return [(e.clientX - r.left) / r.width, (e.clientY - r.top) / r.height];
  }, []);

  // ── Arrastres ──
  const begin = (e, kind, extra = {}) => {
    if (e.button === 2) return;
    e.preventDefault(); e.stopPropagation();
    checkpoint();
    setDrag({ kind, start: toFrac(e), origin: normalizeParams(params), ...extra });
  };

  useEffect(() => {
    if (!drag) return undefined;
    const onMove = (e) => {
      const [x, y] = toFrac(e);
      const o = drag.origin;
      const commit = { commit: false };
      if (drag.kind === 'crop') {
        const c = { ...o.crop };
        const e2 = drag.edge;
        if (e2.includes('l')) c.l = clamp(x * 100, 0, 95 - c.r);
        if (e2.includes('r')) c.r = clamp((1 - x) * 100, 0, 95 - c.l);
        if (e2.includes('t')) c.t = clamp(y * 100, 0, 95 - c.b);
        if (e2.includes('b')) c.b = clamp((1 - y) * 100, 0, 95 - c.t);
        update({ crop: roundCrop(c) }, commit);
      } else if (drag.kind === 'crop-draw') {
        const [sx, sy] = drag.start;
        const l = clamp(Math.min(sx, x), 0, 1); const r = clamp(Math.max(sx, x), 0, 1);
        const t = clamp(Math.min(sy, y), 0, 1); const b = clamp(Math.max(sy, y), 0, 1);
        if (r - l > 0.02 && b - t > 0.02) {
          update({ crop: roundCrop({ l: l * 100, r: (1 - r) * 100, t: t * 100, b: (1 - b) * 100 }) }, commit);
        }
      } else if (drag.kind === 'crop-move') {
        const c = o.crop;
        const dx = (x - drag.start[0]) * 100; const dy = (y - drag.start[1]) * 100;
        const mx = clamp(dx, -c.l, c.r); const my = clamp(dy, -c.t, c.b);
        update({ crop: roundCrop({ l: c.l + mx, r: c.r - mx, t: c.t + my, b: c.b - my }) }, commit);
      } else if (drag.kind === 'warp') {
        const corners = o.warp || CORNERS;
        update({ warp: movePoint(corners, drag.index, x, y, -0.5, 1.5) }, commit);
      } else if (drag.kind === 'poly') {
        update({ shape: { ...o.shape, points: movePoint(o.shape.points, drag.index, x, y) } }, commit);
      } else if (drag.kind === 'fade') {
        const { side } = o.fade;
        const along = side === 'right' ? x : side === 'left' ? 1 - x : side === 'bottom' ? y : 1 - y;
        const v = clamp(along * 100, 0, 100);
        if (drag.which === 'start') update({ fade: { ...o.fade, start: Math.round(clamp(v, 0, 99)) } }, commit);
        else update({ fade: { ...o.fade, soft: Math.round(clamp(v - o.fade.start, 1, 100)) } }, commit);
      } else if (drag.kind === 'mark') {
        setDraft((d) => (d ? { ...d, points: d.type === 'path' ? [...d.points, [x, y]] : [d.points[0], [x, y]] } : d));
      }
    };
    const onUp = () => {
      if (drag.kind === 'mark') {
        const d = draftRef.current;
        if (d && (d.type === 'path' ? d.points.length > 1 : dist(d.points[0], d.points[d.points.length - 1]) > 0.01)) {
          const cur = useImageEditStore.getState().params;
          useImageEditStore.getState().update({ marks: [...cur.marks, d] }, { commit: false });
        }
        setDraft(null);
      }
      setDrag(null);
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    return () => { window.removeEventListener('pointermove', onMove); window.removeEventListener('pointerup', onUp); };
  }, [drag, toFrac, update, setDraft]);

  const onStageDown = (e) => {
    if (e.button === 2) return;
    if (tool === 'recortar') { begin(e, 'crop-draw'); return; }
    if (tool !== 'anotar') return;
    const [x, y] = toFrac(e);
    if (markTool === 'erase') {
      const i = hitMark(params.marks, x, y);
      if (i >= 0) update({ marks: params.marks.filter((_, k) => k !== i) });
      return;
    }
    if (markTool === 'text') { e.preventDefault(); setTextAt([x, y]); setTextVal(''); return; }
    e.preventDefault();
    checkpoint();
    setDraft({ type: markTool, color: markColor, width: markWidth, points: [[x, y]] });
    setDrag({ kind: 'mark', start: [x, y], origin: params });
  };

  const commitText = () => {
    const t = textVal.trim();
    if (t && textAt) update({ marks: [...params.marks, { type: 'text', color: markColor, size: 14 + markWidth * 3, text: t.slice(0, 120), points: [textAt] }] });
    setTextAt(null); setTextVal('');
  };

  // ── Capas de manijas ──
  const crop = params.crop;
  const cr = { l: crop.l / 100, t: crop.t / 100, r: 1 - crop.r / 100, b: 1 - crop.b / 100 };
  const unitPx = Math.max(boxPx.w, boxPx.h) / 1000;
  const fadeGuides = (() => {
    const f = params.fade;
    if (tool !== 'fundido' || f.side === 'none' || f.side === 'radial') return null;
    const a = f.start / 100; const z = Math.min(1, a + f.soft / 100);
    const pos = (v) => (f.side === 'right' || f.side === 'bottom' ? v : 1 - v);
    const vertical = f.side === 'left' || f.side === 'right';
    return { vertical, start: pos(a), end: pos(z) };
  })();
  const polyPts = params.shape.kind === 'polygon' ? (params.shape.points || defaultPolygon(crop)) : null;

  const cursor = tool === 'anotar' ? (markTool === 'erase' ? 'cell' : markTool === 'text' ? 'text' : 'crosshair')
    : tool === 'recortar' ? 'crosshair' : 'default';

  return (
    <div
      ref={wrapRef}
      className={`ie-stage ${className}`}
      style={{ aspectRatio: `${size.width} / ${size.height}`, cursor, ...style }}
      onPointerDown={onStageDown}
      onContextMenu={(e) => e.preventDefault()}
      data-testid="ie-stage"
    >
      <canvas ref={canvasRef} className="ie-canvas" aria-label="Vista previa de la imagen editada" role="img" />

      <svg className="ie-svg" viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden="true">
        {tool === 'recortar' && (
          <>
            <path className="ie-dim" fillRule="evenodd"
              d={`M0 0H100V100H0Z M${cr.l * 100} ${cr.t * 100}H${cr.r * 100}V${cr.b * 100}H${cr.l * 100}Z`} />
            <rect className="ie-outline" x={cr.l * 100} y={cr.t * 100} width={(cr.r - cr.l) * 100} height={(cr.b - cr.t) * 100} />
          </>
        )}
        {tool === 'forma' && polyPts && (
          <>
            <path className="ie-dim" fillRule="evenodd"
              d={`M0 0H100V100H0Z M${polyPts.map(([x, y]) => `${x * 100} ${y * 100}`).join(' L')}Z`} />
            <polygon className="ie-outline" points={polyPts.map(([x, y]) => `${x * 100},${y * 100}`).join(' ')} />
          </>
        )}
        {tool === 'deformar' && (
          <polygon className="ie-outline" points={(params.warp || CORNERS).map(([x, y]) => `${x * 100},${y * 100}`).join(' ')} />
        )}
        {fadeGuides && (fadeGuides.vertical ? (
          <>
            <line className="ie-guide" x1={fadeGuides.start * 100} x2={fadeGuides.start * 100} y1="0" y2="100" />
            <line className="ie-guide is-end" x1={fadeGuides.end * 100} x2={fadeGuides.end * 100} y1="0" y2="100" />
          </>
        ) : (
          <>
            <line className="ie-guide" y1={fadeGuides.start * 100} y2={fadeGuides.start * 100} x1="0" x2="100" />
            <line className="ie-guide is-end" y1={fadeGuides.end * 100} y2={fadeGuides.end * 100} x1="0" x2="100" />
          </>
        ))}
      </svg>

      {/* Marca en curso (el resto ya está en el canvas) */}
      {draft && <DraftMark mark={draft} unitPx={unitPx} />}

      {tool === 'recortar' && (
        <>
          {(crop.l || crop.r || crop.t || crop.b) > 0 && <div className="ie-crop-body" style={{ left: pct(cr.l), top: pct(cr.t), width: pct(cr.r - cr.l), height: pct(cr.b - cr.t) }}
            onPointerDown={(e) => begin(e, 'crop-move')} />}
          {[['l', cr.l, (cr.t + cr.b) / 2], ['r', cr.r, (cr.t + cr.b) / 2], ['t', (cr.l + cr.r) / 2, cr.t], ['b', (cr.l + cr.r) / 2, cr.b],
            ['tl', cr.l, cr.t], ['tr', cr.r, cr.t], ['bl', cr.l, cr.b], ['br', cr.r, cr.b]].map(([edge, x, y]) => (
              <span key={edge} className={`ie-handle ie-h-${edge.length === 1 ? 'edge' : 'corner'}`}
                style={{ left: pct(x), top: pct(y) }} onPointerDown={(e) => begin(e, 'crop', { edge })}
                role="presentation" data-testid={`ie-crop-${edge}`} />
          ))}
        </>
      )}

      {tool === 'deformar' && (params.warp || CORNERS).map(([x, y], i) => (
        <span key={i} className="ie-handle ie-h-warp" style={{ left: pct(x), top: pct(y) }}
          onPointerDown={(e) => begin(e, 'warp', { index: i })} role="presentation" data-testid={`ie-warp-${i}`} />
      ))}

      {tool === 'forma' && polyPts && (
        <>
          <div className="ie-poly-hit" onDoubleClick={(e) => {
            const [x, y] = toFrac(e);
            update({ shape: { ...params.shape, points: insertPolygonPoint(polyPts, x, y) } });
          }} />
          {polyPts.map(([x, y], i) => (
            <span key={i} className="ie-handle ie-h-poly" style={{ left: pct(x), top: pct(y) }}
              onPointerDown={(e) => begin(e, 'poly', { index: i })}
              onContextMenu={(e) => {
                e.preventDefault(); e.stopPropagation();
                if (polyPts.length > 3) update({ shape: { ...params.shape, points: polyPts.filter((_, k) => k !== i) } });
              }}
              role="presentation" data-testid={`ie-poly-${i}`} />
          ))}
        </>
      )}

      {fadeGuides && ['start', 'end'].map((which) => {
        const v = fadeGuides[which];
        const st = fadeGuides.vertical ? { left: pct(v), top: '50%' } : { top: pct(v), left: '50%' };
        return (
          <span key={which} className={`ie-handle ie-h-fade ${which === 'end' ? 'is-end' : ''}`} style={st}
            title={which === 'start' ? 'Dónde empieza' : 'Dónde termina'}
            onPointerDown={(e) => begin(e, 'fade', { which })} role="presentation" data-testid={`ie-fade-${which}`} />
        );
      })}

      {textAt && (
        <input
          className="ie-text-input"
          style={{ left: pct(textAt[0]), top: pct(textAt[1]), color: markColor }}
          autoFocus
          value={textVal}
          maxLength={120}
          placeholder="Escribí y Enter"
          onPointerDown={(e) => e.stopPropagation()}
          onChange={(e) => setTextVal(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') commitText(); if (e.key === 'Escape') { setTextAt(null); setTextVal(''); } e.stopPropagation(); }}
          onBlur={commitText}
          aria-label="Texto de la anotación"
          data-testid="ie-text-input"
        />
      )}
    </div>
  );
}

const dist = (a, b) => Math.hypot(a[0] - b[0], a[1] - b[1]);
const roundCrop = (c) => ({ l: +c.l.toFixed(1), r: +c.r.toFixed(1), t: +c.t.toFixed(1), b: +c.b.toFixed(1) });

function DraftMark({ mark, unitPx }) {
  const sw = Math.max(1, mark.width * unitPx);
  const pts = mark.points;
  const a = pts[0]; const z = pts[pts.length - 1];
  return (
    <svg className="ie-svg ie-draft" width="100%" height="100%" aria-hidden="true">
      <g stroke={mark.color} strokeWidth={sw} fill="none" strokeLinecap="round" strokeLinejoin="round">
        {mark.type === 'path' && pts.length > 1 && pts.slice(1).map((p, i) => (
          <line key={i} x1={`${pts[i][0] * 100}%`} y1={`${pts[i][1] * 100}%`} x2={`${p[0] * 100}%`} y2={`${p[1] * 100}%`} />
        ))}
        {mark.type === 'arrow' && <line x1={`${a[0] * 100}%`} y1={`${a[1] * 100}%`} x2={`${z[0] * 100}%`} y2={`${z[1] * 100}%`} />}
        {mark.type === 'rect' && (
          <rect x={`${Math.min(a[0], z[0]) * 100}%`} y={`${Math.min(a[1], z[1]) * 100}%`}
            width={`${Math.abs(z[0] - a[0]) * 100}%`} height={`${Math.abs(z[1] - a[1]) * 100}%`} />
        )}
        {mark.type === 'ellipse' && (
          <ellipse cx={`${((a[0] + z[0]) / 2) * 100}%`} cy={`${((a[1] + z[1]) / 2) * 100}%`}
            rx={`${(Math.abs(z[0] - a[0]) / 2) * 100}%`} ry={`${(Math.abs(z[1] - a[1]) / 2) * 100}%`} />
        )}
      </g>
    </svg>
  );
}
